import { IconChevronRight } from '@tabler/icons-react';
import { Loader, Text } from '@wapharma/design-system';
import {
  buildReferenceMentionMenuEntries,
  getMessageReferenceEntityKind,
  isSelectableMentionEntry,
  parseReferenceMentionQuery,
  type MessageReferenceMenuEntry,
  type MessageReferenceSuggestion,
} from '../lib/messageReferenceSearch';
import { EntityToneWell, EntityTypeMark } from './EntityTypeMark';
import styles from './MessageReferenceMentionMenu.module.css';

type MessageReferenceMentionMenuProps = {
  open: boolean;
  loading: boolean;
  query: string;
  items: MessageReferenceSuggestion[];
  activeIndex: number;
  onPickEntityType: (refType: string) => void;
  onPickResult: (item: MessageReferenceSuggestion) => void;
  onHover: (index: number) => void;
};

function splitResultLabel(label: string): { title: string; meta?: string } {
  const sep = label.indexOf(' · ');
  if (sep < 0) return { title: label };
  return {
    title: label.slice(0, sep),
    meta: label.slice(sep + 3),
  };
}

export function MessageReferenceMentionMenu({
  open,
  loading,
  query,
  items,
  activeIndex,
  onPickEntityType,
  onPickResult,
  onHover,
}: MessageReferenceMentionMenuProps) {
  if (!open) return null;

  const parsed = parseReferenceMentionQuery(query);
  const entries = buildReferenceMentionMenuEntries(parsed, items);
  let selectableIndex = 0;

  const renderEmpty = () => {
    if (loading) {
      return (
        <div className={styles.rowMuted}>
          <Loader size="sm" />
        </div>
      );
    }
    if (parsed.entityType && !parsed.searchTerm) {
      const kind = getMessageReferenceEntityKind(parsed.entityType);
      return (
        <div className={styles.rowMuted}>
          <Text size="sm" c="dimmed">
            {kind ? `Rechercher un ${kind.label.toLowerCase()}…` : 'Tapez pour chercher…'}
          </Text>
        </div>
      );
    }
    return (
      <div className={styles.rowMuted}>
        <Text size="sm" c="dimmed">
          {query.trim() ? 'Aucun résultat' : 'Choisir un type de ressource'}
        </Text>
      </div>
    );
  };

  return (
    <div className={styles.menu} role="listbox" aria-label="Références">
      <div className={styles.menuScroll}>
        {entries.length ? (
          entries.map((entry: MessageReferenceMenuEntry) => {
            if (entry.kind === 'header') {
              return (
                <div key={`header-${entry.refType}`} className={styles.sectionHeader}>
                  {entry.label}
                </div>
              );
            }

            const isActive = selectableIndex === activeIndex;
            const currentSelectable = selectableIndex;
            selectableIndex += 1;

            if (entry.kind === 'entity-type') {
              return (
                <button
                  key={`type-${entry.refType}`}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={`${styles.row} ${isActive ? styles.rowActive : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => onHover(currentSelectable)}
                  onClick={() => onPickEntityType(entry.refType)}
                >
                  <span className={styles.copy}>
                    <EntityTypeMark entityType={entry.refType} size="md" />
                    <span className={styles.description}>{entry.description}</span>
                  </span>
                  <IconChevronRight size={15} stroke={1.75} aria-hidden className={styles.chevron} />
                </button>
              );
            }

            const { title, meta } = splitResultLabel(entry.label);
            const kind = getMessageReferenceEntityKind(entry.refType);

            return (
              <button
                key={`${entry.refType}-${entry.refId}`}
                type="button"
                role="option"
                aria-selected={isActive}
                className={`${styles.row} ${isActive ? styles.rowActive : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => onHover(currentSelectable)}
                onClick={() => onPickResult(entry)}
              >
                <span className={styles.iconWrap}>
                  <EntityToneWell entityType={entry.refType} size="sm" />
                </span>
                <span className={styles.copy}>
                  <span className={styles.title}>{title}</span>
                  {meta ? (
                    <span className={styles.meta}>{meta}</span>
                  ) : kind ? (
                    <span className={styles.meta}>
                      <EntityTypeMark entityType={entry.refType} label={kind.label} />
                    </span>
                  ) : null}
                </span>
              </button>
            );
          })
        ) : (
          renderEmpty()
        )}
      </div>
    </div>
  );
}

export function getSelectableMentionEntries(
  query: string,
  items: MessageReferenceSuggestion[],
): Extract<MessageReferenceMenuEntry, { kind: 'entity-type' | 'result' }>[] {
  return buildReferenceMentionMenuEntries(parseReferenceMentionQuery(query), items).filter(
    isSelectableMentionEntry,
  );
}
